import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { ExclamationTriangleIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { format, parseISO } from 'date-fns';
import JournalForm from './forms/JournalForm';
import { JournalEntry } from '../types/journal';
import { Button, Alert, Spinner } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

// Define emotions and their corresponding emojis
const emotionsMap: { [key: string]: string } = {
  happy: '😊',
  sad: '😢',
  angry: '😠',
  surprised: '😮',
  calm: '😌',
  anxious: '😟',
  excited: '🤩',
  tired: '😴',
  confused: '😕',
};

// Fallback data
const fallbackEntries: JournalEntry[] = [
    { id: 1, title: "Reflections on the Week", content: "It was a productive week overall...", date: "2024-08-14T10:00:00Z", emotion: "" },
    { id: 2, content: "Quick thoughts on the new project kickoff meeting.", date: "2024-08-13T15:30:00Z", emotion: "" },
];

interface FilterOptions {
  startDate: string;
  endDate: string;
  businessId: string;
  projectId: string;
  categoryId: string;
  entryType: string;
}

interface JournalListProps {
  entryIdToOpen?: number | null;
  onEntryOpened?: () => void;
  filters?: FilterOptions;
}

const JournalList: React.FC<JournalListProps> = ({ entryIdToOpen, onEntryOpened, filters }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Delete Confirmation Modal State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [entryToDeleteId, setEntryToDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [expandedEntries, setExpandedEntries] = useState<{ [id: number]: boolean }>({});
  const [editEntry, setEditEntry] = useState<JournalEntry | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchJournalEntries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/journal_entries/');
      let filteredData = response.data || [];

      // Apply client-side filters
      if (filters) {
        filteredData = filteredData.filter((entry: JournalEntry) => {
          // Date range filter
          if (filters.startDate && entry.date) {
            const entryDate = new Date(entry.date);
            const startDate = new Date(filters.startDate);
            if (entryDate < startDate) return false;
          }
          if (filters.endDate && entry.date) {
            const entryDate = new Date(entry.date);
            const endDate = new Date(filters.endDate);
            // Set end date to end of day for inclusive comparison
            endDate.setHours(23, 59, 59, 999);
            if (entryDate > endDate) return false;
          }

          // Business filter
          if (filters.businessId && entry.business?.id !== parseInt(filters.businessId)) {
            return false;
          }

          // Project filter
          if (filters.projectId && entry.project?.id !== parseInt(filters.projectId)) {
            return false;
          }

          // Category filter
          if (filters.categoryId) {
            const categoryIdToMatch = parseInt(filters.categoryId);
            // Check if entry has categories array and if it includes the selected category
            const entryCategories = entry.categories || [];
            const hasCategoryId = entryCategories.some(
              cat => cat && cat.id === categoryIdToMatch
            );
            if (!hasCategoryId) return false;
          }

          // Entry Type filter
          if (filters.entryType && entry.entry_type !== filters.entryType) {
            return false;
          }

          return true;
        });
      }

      setEntries(filteredData);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setError('Failed to load journal entries');
      setEntries(fallbackEntries);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchJournalEntries();
  }, [fetchJournalEntries]);

  // Automatically open entry when entryIdToOpen is provided
  useEffect(() => {
    if (entryIdToOpen && entries.length > 0) {
      const entryToEdit = entries.find(e => e.id === entryIdToOpen);
      if (entryToEdit) {
        handleEditEntry(entryToEdit);
        if (onEntryOpened) {
          onEntryOpened();
        }
      }
    }
  }, [entryIdToOpen, entries, onEntryOpened]);

  // Delete Modal Handlers
  const openConfirmModal = (id: number) => {
      setEntryToDeleteId(id);
      setDeleteError(null); // Clear previous errors
      setIsConfirmOpen(true);
  };

  const closeConfirmModal = useCallback(() => {
      if (isDeleting) return; // Prevent closing while delete is in progress
      setIsConfirmOpen(false);
      setTimeout(() => {
          setEntryToDeleteId(null);
          setDeleteError(null);
      }, 300); // Delay reset to allow modal fade out
  }, [isDeleting]);

  const handleDeleteEntry = async () => {
      if (entryToDeleteId === null) return;
      setIsDeleting(true);
      setDeleteError(null);
      try {
          const response = await apiClient.delete(`/api/journal_entries/${entryToDeleteId}/`);

          if (response.status >= 200 && response.status < 300) {
              // Remove entry from local state on success
              setEntries(currentEntries => currentEntries.filter(entry => entry.id !== entryToDeleteId));
              closeConfirmModal();
          } else {
              throw new Error(`Failed to delete entry (${response.status})`);
          }
      } catch (err: any) {
          console.error("Error deleting journal entry:", err);
          const errorMsg = err.message || 'Failed to delete entry.';
          setDeleteError(errorMsg);
          // Keep modal open to show error
      } finally {
          setIsDeleting(false);
      }
  };

  const formatDate = (dateString: string) => {
     if (!dateString) return "No Date";
     try {
        // Format date and time
        // Format just the date part (YYYY-MM-DD from Django DateField)
        return format(parseISO(dateString), 'MMM d, yyyy');
     } catch (e) {
         console.error("Error formatting date:", dateString, e);
         return "Invalid Date";
     }
  }

  // Sort entries by date desc, then id desc for same date
  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.date ?? "").getTime();
    const dateB = new Date(b.date ?? "").getTime();
    if (dateA !== dateB) return dateB - dateA;
    return b.id - a.id;
  });

  const handleEditEntry = (entry: JournalEntry) => {
    setEditEntry({
      ...entry,
      created_at: entry.created_at || entry.date || new Date().toISOString(),
      emotion: entry.emotion ?? "",
    });
    setIsEditOpen(true);
  };

  const handleJournalEntryUpdated = (updatedEntry: JournalEntry) => {
    setEntries(currentEntries => currentEntries.map(e => e.id === updatedEntry.id ? updatedEntry : e));
    setIsEditOpen(false);
    setEditEntry(null);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      {entries.length === 0 && !error && !loading && (
         <p style={{
           textAlign: 'center',
           color: COLORS.text.secondary,
           paddingTop: SPACING[16],
           paddingBottom: SPACING[16],
           fontSize: TYPOGRAPHY.size.base
         }}>No journal entries found.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        {sortedEntries.map((entry) => {
          const isExpanded = expandedEntries[entry.id];
          return (
            <div
              key={entry.id}
              onClick={() => navigate(`/journal/${entry.id}`)}
              style={{
                backgroundColor: COLORS.bg.panel,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                padding: SPACING[16],
                transition: 'box-shadow 150ms ease',
                cursor: 'pointer'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: SPACING[4]
              }}>
                  {/* Title and Emotion */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Display emoji if emotion exists */}
                    {entry.emotion && emotionsMap[entry.emotion] && (
                      <span style={{ fontSize: TYPOGRAPHY.size['2xl'], marginRight: SPACING[8] }} title={entry.emotion}>{emotionsMap[entry.emotion]}</span>
                    )}
                    {/* Updated Title Logic - Remove "Entry - " */}
                    <h3 style={{
                      fontSize: TYPOGRAPHY.size.base,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      color: COLORS.text.primary
                    }}>{entry.title || formatDate(entry.date ?? "")}</h3>
                  </div>
                  {/* Right side: Date, Edit, and Delete Button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <p style={{
                        fontSize: TYPOGRAPHY.size.xs,
                        color: COLORS.text.tertiary,
                        whiteSpace: 'nowrap'
                      }}>{formatDate(entry.date ?? "")}</p>
                      {/* Edit Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEditEntry(entry); }}
                        style={{
                          color: COLORS.text.tertiary,
                          padding: '2px',
                          height: 'auto'
                        }}
                        title="Edit Entry"
                        aria-label="Edit journal entry"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ height: '16px', width: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3zm0 0v3h3" /></svg>
                      </Button>
                      {/* Copy Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(entry.content);
                        }}
                        style={{
                          color: COLORS.text.tertiary,
                          padding: '2px',
                          height: 'auto'
                        }}
                        title="Copy Content"
                        aria-label="Copy journal entry content"
                      >
                        <ClipboardDocumentIcon style={{ width: '16px', height: '16px' }} />
                      </Button>
                      {/* Delete Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openConfirmModal(entry.id); }}
                        style={{
                          color: COLORS.text.tertiary,
                          padding: '2px',
                          height: 'auto'
                        }}
                        title="Delete Entry"
                        aria-label="Delete journal entry"
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </Button>
                  </div>
              </div>
              <p style={{
                fontSize: TYPOGRAPHY.size.sm,
                color: COLORS.text.secondary,
                whiteSpace: 'pre-wrap',
                ...(isExpanded ? {} : {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden'
                })
              }}>{entry.content}</p>
              {entry.content.split('\n').length > 3 || entry.content.length > 200 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  style={{
                    marginTop: SPACING[4],
                    height: 'auto',
                    paddingLeft: 0,
                    paddingRight: 0,
                    fontSize: TYPOGRAPHY.size.xs,
                    color: COLORS.accent.default,
                    fontWeight: TYPOGRAPHY.weight.medium
                  }}
                  onClick={(e) => { e.stopPropagation(); setExpandedEntries(prev => ({ ...prev, [entry.id]: !isExpanded })); }}
                  aria-expanded={isExpanded}
                  rightIcon={isExpanded ? <ChevronUpIcon style={{ width: '16px', height: '16px' }} /> : <ChevronDownIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Edit Journal Entry Modal */}
      <JournalForm
        isOpen={isEditOpen}
        onClose={() => { setIsEditOpen(false); setEditEntry(null); }}
        onJournalEntryCreated={() => {}}
        initialEntry={editEntry}
        isEditMode={true}
        onJournalEntryUpdated={handleJournalEntryUpdated}
      />

      {/* Delete Confirmation Modal */}
      <Transition appear show={isConfirmOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={closeConfirmModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)'
            }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
              textAlign: 'center'
            }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: '448px',
                  transform: 'none',
                  overflow: 'hidden',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  transition: 'all 150ms ease'
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size.lg,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    lineHeight: '1.5',
                    color: COLORS.text.primary,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <ExclamationTriangleIcon style={{ width: '24px', height: '24px', color: COLORS.status.error, marginRight: SPACING[8] }} />
                    Confirm Deletion
                  </Dialog.Title>
                  <div style={{ marginTop: SPACING[12] }}>
                    <p style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.secondary
                    }}>
                      Are you sure you want to delete this journal entry? This action cannot be undone.
                    </p>
                  </div>

                  {deleteError && (
                    <Alert variant="error" style={{ marginTop: SPACING[16] }}>
                      {deleteError}
                    </Alert>
                  )}

                  <div style={{
                    marginTop: SPACING[24],
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: SPACING[12]
                  }}>
                    <Button
                      variant="outline"
                      size="md"
                      fullWidth
                      onClick={closeConfirmModal}
                      disabled={isDeleting}
                      style={{
                        border: `1px solid ${COLORS.border.default}`,
                        backgroundColor: COLORS.bg.panel,
                        color: COLORS.text.primary
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="md"
                      fullWidth
                      onClick={handleDeleteEntry}
                      disabled={isDeleting}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Confirm Delete
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

    </div>
  );
};

export default JournalList;
